import { OptionallyVisible } from 'components';
import { FieldArray, getIn, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import { SelectItem } from 'components/inputs/select/select';

import { FORMIK_INITIAL_VALUES, FORM_FIELDS, LABELS } from '../../keys';
import { Section, SectionRow } from '../../styles';
import { BridgeAccountPurpose } from '../../types';
import Checkbox from '../checkbox/checkbox';
import { FieldStatusBadge } from '../fieldStatusBadge/FieldStatusBadge';
import FormikInput from '../formikTextInput/formikTextInput';
import AccountPurpose from './components/accountPurpose/accountPurpose';
import EstimatedAnnualRevenue from './components/estimatedAnnualRevenue/estimatedAnnualRevenue';
import HighRiskActivities from './components/hiRiskActivities/hiRiskActivities';
import RegulatedActivity from './components/regulatedActivity/regulatedActivity';
import SourceOfFunds from './components/sourceOfFunds/sourceOfFunds';
import { Container, Delimeter } from './styles';

interface StepFormProps {
  error?: Record<string, string>;
  countries: SelectItem[];
  isPartialMode?: boolean;
}

export default ({ error, countries, isPartialMode = false }: StepFormProps) => {
  const { t } = useTranslation();

  const { values } = useFormikContext<typeof FORMIK_INITIAL_VALUES>();
  const accountPurposeInformationValue = getIn(values, FORM_FIELDS.ACCOUNT_PURPOSE) as BridgeAccountPurpose;

  return (
    <Container>
      <FieldArray
        render={({ insert, remove }) => (
          <HighRiskActivities
            error={error}
            insert={insert}
            remove={remove}
            fieldPath={FORM_FIELDS.HIGH_RISK_ACTIVITIES}
            isPartialMode={isPartialMode}
          />
        )}
        name={FORM_FIELDS.HIGH_RISK_ACTIVITIES}
      />

      <Delimeter />

      <Section>
        <RegulatedActivity
          title={LABELS.SECTIONS.REGULATED_ACTIVITY.TITLE}
          fieldPath={FORM_FIELDS.REGULATED_ACTIVITY}
          countries={countries}
          error={error}
          isPartialMode={isPartialMode}
        />
      </Section>

      <Delimeter />

      <SectionRow>
        <Section>
          <SourceOfFunds fieldPath={FORM_FIELDS.SOURCE_OF_FUNDS} error={error} isPartialMode={isPartialMode} />
        </Section>
        <FieldStatusBadge fieldName={FORM_FIELDS.SOURCE_OF_FUNDS} />
      </SectionRow>

      <Section>
        <FormikInput
          label={t(LABELS.SECTIONS.SOURCE_OF_FUNDS_DESCRIPTION.TITLE)}
          fieldPath={FORM_FIELDS.SOURCE_OF_FUNDS_DESCRIPTION}
          placeholder={t(LABELS.SECTIONS.SOURCE_OF_FUNDS_DESCRIPTION.PLACEHOLDER)}
          error={error}
          isOptional
        />
      </Section>

      <SectionRow>
        <Section>
          <AccountPurpose fieldPath={FORM_FIELDS.ACCOUNT_PURPOSE} error={error} isPartialMode={isPartialMode} />
        </Section>
        <FieldStatusBadge fieldName={FORM_FIELDS.ACCOUNT_PURPOSE} />
      </SectionRow>

      <OptionallyVisible visible={accountPurposeInformationValue === BridgeAccountPurpose.OTHER}>
        <Section>
          <FormikInput
            label={t(LABELS.SECTIONS.ACCOUNT_PURPOSE_OTHER.TITLE)}
            fieldPath={FORM_FIELDS.ACCOUNT_PURPOSE_OTHER}
            placeholder={t(LABELS.SECTIONS.ACCOUNT_PURPOSE_OTHER.PLACEHOLDER)}
            error={error}
          />
        </Section>
      </OptionallyVisible>

      <Section>
        <EstimatedAnnualRevenue fieldPath={FORM_FIELDS.ESTIMATED_ANNUAL_REVENUE_USD} error={error} isPartialMode={isPartialMode} />
      </Section>

      <Section>
        <FormikInput
          label={t(LABELS.SECTIONS.EXPECTED_MONTHLY_PAYMENTS_USD.TITLE)}
          fieldPath={FORM_FIELDS.EXPECTED_MONTHLY_PAYMENTS_USD}
          placeholder={t(LABELS.SECTIONS.EXPECTED_MONTHLY_PAYMENTS_USD.PLACEHOLDER)}
          error={error}
          isOptional
        />
      </Section>

      <Checkbox
        text={t(LABELS.SECTIONS.OPERATES_IN_PROHIBITED_COUNTRIES.TEXT)}
        label={t(LABELS.SECTIONS.OPERATES_IN_PROHIBITED_COUNTRIES.LABEL)}
        fieldPath={FORM_FIELDS.OPERATES_IN_PROHIBITED_COUNTRIES}
        isOptional
        isPartialMode={isPartialMode}
      />
    </Container>
  );
};
